<template>
  <div class="ctc-step">
    <div class="ctc-step__eyebrow">PHASE 1 · ÉTAPE 2 SUR 4</div>
    <h2 class="ctc-step__title">Widget et affiliation Affilizz</h2>
    <p class="ctc-affilizz__intro">
      Composez le widget, puis vérifiez l’affiliation. Les contenus Affilizz
      créés ici sont ceux de l’article : la signature, le format et la
      publication se choisissent ensuite.
    </p>

    <section class="ctc-affilizz" aria-label="Widget Affilizz">
      <ctc-similar-products-option
        :value="widgetEnabled"
        :widget-type="widgetType"
        :similar="similar"
        :similar-actions="similarActions"
        :created-widget-excluded="createdWidgetExcluded"
        :focus-selection="selectionFocus"
        @input="$emit('update:widgetEnabled', $event)"
        @update:widgetType="$emit('update:widgetType', $event)"
      />
    </section>

    <!-- One box, one state: idle, running, stale, verified, failed. -->
    <section
      class="ctc-check"
      :class="`ctc-check--${box.tone}`"
      aria-labelledby="ctc-check-title"
    >
      <header class="ctc-check__head">
        <span class="ctc-check__shield" aria-hidden="true">
          <v-icon :color="box.color">{{ icons.mdiShieldCheckOutline }}</v-icon>
        </span>
        <span class="ctc-check__copy" role="status" aria-live="polite">
          <strong id="ctc-check-title">{{ box.title }}</strong>
          <small>{{ box.subtitle }}</small>
        </span>
        <span v-if="box.pill" class="ctc-check__pill">
          <v-icon v-if="box.pillIcon" x-small color="currentColor">
            {{ box.pillIcon }}
          </v-icon>
          {{ box.pill }}
        </span>
        <rmr-action-button
          v-if="box.verifyLabel"
          :label="box.verifyLabel"
          :icon="
            verificationStale ? icons.mdiRefresh : icons.mdiShieldCheckOutline
          "
          size="sm"
          :disabled="!canVerify"
          :loading="verification.running"
          class="ctc-check__verify"
          @click="$emit('verify')"
        />
      </header>

      <p
        v-if="verification.error"
        class="ctc-check__banner ctc-check__banner--error"
        role="alert"
      >
        <v-icon x-small color="currentColor">{{ icons.mdiClose }}</v-icon>
        <span>{{ verification.error }}</span>
      </p>
      <p
        v-if="box.banner"
        class="ctc-check__banner"
        :class="`ctc-check__banner--${box.bannerTone}`"
      >
        <v-icon x-small color="currentColor">{{ box.bannerIcon }}</v-icon>
        <span
          ><b>{{ box.bannerTitle }}</b> {{ box.banner }}</span
        >
      </p>
      <p
        v-if="contentsIssue && !verification.running"
        class="ctc-check__banner ctc-check__banner--warning"
      >
        <v-icon x-small color="currentColor">{{
          icons.mdiAlertOutline
        }}</v-icon>
        <span>{{ contentsIssue }}</span>
      </p>

      <ul class="ctc-check__rows">
        <li
          v-for="row in rows"
          :key="row.key"
          class="ctc-check__row"
          :class="`ctc-check__row--${row.state}`"
        >
          <span
            class="ctc-check__state"
            :class="`ctc-check__state--${row.state}`"
            aria-hidden="true"
          >
            <v-progress-circular
              v-if="row.state === 'running'"
              indeterminate
              size="18"
              width="2"
              color="primary"
            />
            <v-icon v-else-if="row.icon" x-small color="currentColor">
              {{ row.icon }}
            </v-icon>
          </span>
          <span class="ctc-check__thumb">
            <img
              v-if="row.image"
              :src="row.image"
              alt=""
              width="44"
              height="44"
              loading="lazy"
            />
            <v-icon v-else :color="colors.muted">{{ row.thumbIcon }}</v-icon>
          </span>
          <span class="ctc-check__info">
            <span class="ctc-check__title">
              {{ row.title }}
              <span class="ctc-check__tag">{{ row.tag }}</span>
            </span>
            <span class="ctc-check__desc" :title="row.descTitle">
              <b v-if="row.lead" :class="`ctc-check__lead--${row.state}`">
                {{ row.lead }}
              </b>
              {{ row.desc }}
            </span>
          </span>
          <span class="ctc-check__actions">
            <template
              v-if="
                row.key === 'main' && rowActions && offers('choose-product')
              "
            >
              <rmr-action-button
                label="Choisir un autre produit"
                :icon="icons.mdiSwapHorizontal"
                size="xs"
                :disabled="busy"
                @click="$emit('choose-product')"
              />
            </template>
            <template
              v-else-if="row.key === 'widget' && rowActions && widgetActions"
            >
              <rmr-action-button
                v-if="offers('disable-widget')"
                label="Continuer sans widget"
                variant="ghost"
                size="xs"
                :disabled="busy"
                @click="$emit('disable-widget')"
              />
              <rmr-action-button
                v-if="offers('edit-widget')"
                label="Modifier le widget"
                :icon="icons.mdiPencil"
                :variant="guidance.tone === 'error' ? 'primary' : 'ghost'"
                size="xs"
                :disabled="busy"
                @click="editWidget"
              />
            </template>
            <span
              v-else-if="row.status"
              class="ctc-check__status"
              :class="`ctc-check__status--${row.state}`"
            >
              {{ row.status }}
            </span>
            <span
              v-else-if="row.state === 'running'"
              class="ctc-check__skeleton"
            />
          </span>
        </li>
      </ul>

      <footer class="ctc-check__foot">
        <span class="ctc-check__foot-text">
          {{ box.foot }}
          <template v-if="affiliation && offers('recheck')">
            <br />
            <small>{{ recheckHelp }}</small>
          </template>
        </span>
        <rmr-action-button
          v-if="affiliation && offers('recheck')"
          :label="
            recheckWait
              ? `Relire l’affiliation (dans ${recheckWait} s)`
              : 'Relire l’affiliation'
          "
          :icon="icons.mdiRefresh"
          variant="ghost"
          size="xs"
          :loading="verification.rechecking"
          :disabled="busy || recheckWait > 0"
          @click="$emit('recheck')"
        />
      </footer>
    </section>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, ref, watch } from "@vue/composition-api";
import {
  mdiAlertOutline,
  mdiCheck,
  mdiClockOutline,
  mdiClose,
  mdiExclamation,
  mdiMinus,
  mdiPencil,
  mdiRefresh,
  mdiShieldCheckOutline,
  mdiShoppingOutline,
  mdiSwapHorizontal,
  mdiViewGridOutline,
} from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import {
  RMR_CA_DARK,
  RMR_DANGER,
  RMR_GREEN_DARK,
  RMR_INDIGO,
  RMR_TEXT_MUTED,
} from "@/styles/tokens";

import { AFFILIATION_RECHECK_HELP } from "../constants";
import {
  affiliationGuidance,
  affilizzIssueOf,
  checkedAgoLabel,
  componentStateOf,
  formatPrice,
  merchantLabel,
  offerCountLabel,
  parseUtcDate,
  selectionCountLabel,
  verificationChangesOf,
} from "../ctc-model";
import CtcSimilarProductsOption from "./CtcSimilarProductsOption.vue";

export default {
  name: "CtcAffilizzStep",
  components: {
    CtcSimilarProductsOption,
    RmrActionButton,
  },
  props: {
    site: {
      type: Object,
      default: null,
    },
    // useCtcCreateFlow().state.product: shown on the main product row.
    product: {
      type: Object,
      default: null,
    },
    widgetEnabled: {
      type: Boolean,
      default: true,
    },
    widgetType: {
      type: String,
      default: "BOX",
    },
    // useCtcCreateFlow().state.similar and its similarActions.
    similar: {
      type: Object,
      required: true,
    },
    similarActions: {
      type: Object,
      required: true,
    },
    offerCheck: {
      type: Object,
      default: null,
    },
    checkingOffer: {
      type: Boolean,
      default: false,
    },
    offerCheckError: {
      type: String,
      default: null,
    },
    // useCtcCreateFlow().state.verification
    verification: {
      type: Object,
      required: true,
    },
    // Affiliation of the current selection; null if not (or no longer) verified.
    affiliation: {
      type: Object,
      default: null,
    },
    verificationStale: {
      type: Boolean,
      default: false,
    },
    canVerify: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    // The backend states decide everything shown and offered.
    const guidance = computed(() => affiliationGuidance(props.affiliation));
    const offers = (action) => guidance.value.actions.includes(action);

    const busy = computed(
      () => props.verification.running || props.verification.rechecking
    );

    // The server allows one re-read a minute: the button waits for it. A local
    // clock only, nothing is asked to Affilizz meanwhile.
    const now = ref(Date.now());
    let clock = null;
    const stopClock = () => {
      if (clock) clearInterval(clock);
      clock = null;
    };
    const recheckAt = computed(() => {
      const date = parseUtcDate(
        props.affiliation && props.affiliation.recheckAvailableAt
      );
      return date ? date.getTime() : 0;
    });
    const recheckWait = computed(() =>
      Math.max(0, Math.ceil((recheckAt.value - now.value) / 1000))
    );
    watch(
      recheckAt,
      (at) => {
        now.value = Date.now();
        stopClock();
        if (at > now.value) {
          clock = setInterval(() => {
            now.value = Date.now();
            if (now.value >= at) stopClock();
          }, 1000);
        }
      },
      { immediate: true }
    );
    onBeforeUnmount(stopClock);

    // "Vérifié il y a N min": a coarse clock, local only.
    const minuteNow = ref(Date.now());
    const minuteClock = setInterval(() => {
      minuteNow.value = Date.now();
    }, 30000);
    onBeforeUnmount(() => clearInterval(minuteClock));

    const siteName = computed(() =>
      props.site ? props.site.name : "le site choisi"
    );
    const previous = computed(
      () =>
        (props.verification.article &&
          props.verification.article.affiliation) ||
        null
    );
    const changes = computed(() =>
      verificationChangesOf(props.verification.article, {
        siteId: props.site && props.site.id,
        product: props.product,
        widgetEnabled: props.widgetEnabled,
        widgetType: props.widgetType,
        groups: props.similar.selected,
      })
    );
    const ago = (value) => checkedAgoLabel(value, minuteNow.value);

    // State of one component from the backend booleans (null = waiting).
    const stateOf = (value, included = true) =>
      ({
        affiliated: "ok",
        not_affiliated: "error",
        pending: "pending",
        disabled: "off",
      }[componentStateOf(value, included)]);

    const box = computed(() => {
      const site = siteName.value;
      const withWidget = props.widgetEnabled;
      if (props.verification.running) {
        return {
          tone: "running",
          color: RMR_INDIGO,
          title: "Vérification en cours…",
          subtitle: `Création et lecture du bouton d’offre${
            withWidget ? " et du widget" : ""
          } sur ${site}.`,
          foot: "Cela prend généralement quelques secondes.",
        };
      }
      if (props.verificationStale) {
        const checked = previous.value && previous.value.checkedAt;
        // Without a kept draft (refused as not affiliated), nothing tells
        // what changed and nothing is replaced.
        const kept = !!props.verification.article;
        let subtitle = "La sélection a changé depuis la dernière vérification.";
        if (kept) {
          subtitle = changes.value.product
            ? "Le produit ou le site a changé depuis la dernière vérification."
            : "Le widget a été modifié depuis la dernière vérification.";
        }
        return {
          tone: "stale",
          color: RMR_CA_DARK,
          title: "Vérification à relancer",
          subtitle,
          verifyLabel: "Revérifier",
          foot: [
            checked ? `Dernière vérification ${ago(checked)}` : "",
            kept
              ? "La revérification remplace les contenus Affilizz de la précédente."
              : "",
          ]
            .filter(Boolean)
            .join(" · "),
        };
      }
      const aff = props.affiliation;
      if (!aff) {
        return {
          tone: "idle",
          color: RMR_INDIGO,
          title: "Vérification de l’affiliation Affilizz",
          subtitle: `Contrôle le bouton d’offre${
            withWidget ? " et le widget" : ""
          } sur ${site}.`,
          verifyLabel: "Vérifier l’affiliation Affilizz",
          foot: "Les contenus Affilizz de l’article sont créés une seule fois, puis leur affiliation est lue.",
        };
      }
      const g = guidance.value;
      const required = aff.widgetIncluded ? 2 : 1;
      const affiliated =
        (aff.link === true ? 1 : 0) +
        (aff.widgetIncluded && aff.widget === true ? 1 : 0);
      const foot = aff.checkedAt ? `Vérifié ${ago(aff.checkedAt)}` : "";
      if (g.tone === "success") {
        return {
          tone: "ok",
          color: RMR_GREEN_DARK,
          title: "Affiliation Affilizz vérifiée",
          subtitle: `Bouton d’offre${
            aff.widgetIncluded ? " et widget contrôlés" : " contrôlé"
          } sur ${site}.`,
          pill: `${affiliated}/${required} affilié${affiliated > 1 ? "s" : ""}`,
          pillIcon: mdiCheck,
          foot,
        };
      }
      if (g.tone === "error") {
        const missing = required - affiliated;
        return {
          tone: "error",
          color: RMR_DANGER,
          title: "Affiliation incomplète",
          subtitle: `${missing} élément${
            missing > 1 ? "s ne sont" : " n’est"
          } pas affilié${missing > 1 ? "s" : ""} pour ${site}.`,
          pill: `${affiliated}/${required} affilié${affiliated > 1 ? "s" : ""}`,
          pillIcon: mdiClose,
          banner: g.message,
          bannerTitle: `${g.title} :`,
          bannerTone: "error",
          bannerIcon: mdiClose,
          foot,
        };
      }
      return {
        tone: "pending",
        color: RMR_CA_DARK,
        title: "Affiliation en attente",
        subtitle: `Affilizz n’a pas encore tout confirmé pour ${site}.`,
        pill: "En attente",
        pillIcon: mdiClockOutline,
        banner: g.message,
        bannerTitle: `${g.title} :`,
        bannerTone: "warning",
        bannerIcon: mdiClockOutline,
        foot,
      };
    });

    const STATUS = {
      ok: { text: "Affilié", icon: mdiCheck },
      error: { text: "Non affilié", icon: mdiClose },
      pending: { text: "En attente", icon: mdiClockOutline },
      stale: { text: "À revérifier", icon: mdiExclamation },
      idle: { text: "À vérifier", icon: null },
      off: { text: "Désactivé", icon: mdiMinus },
      running: { text: "", icon: null },
    };
    const rowOf = (row) => ({
      ...row,
      status: STATUS[row.state].text,
      icon: STATUS[row.state].icon,
    });

    const rows = computed(() => {
      const tone = box.value.tone;
      const product = props.product || {};
      const productDesc = [
        product.title,
        product.merchant ? merchantLabel(product.merchant) : "",
        typeof product.priceCurrent === "number"
          ? `Dès ${formatPrice(product.priceCurrent, product.currency)}`
          : "",
      ]
        .filter(Boolean)
        .join(" · ");
      const offers = props.similar.selected
        .flatMap((group) => group.products)
        .reduce((sum, item) => sum + (item.offerCount || 0), 0);
      const widgetDesc = props.widgetEnabled
        ? `${selectionCountLabel(props.similar.selected)} · ${offerCountLabel(
            offers
          )}`
        : "Il ne sera pas intégré dans l’article.";

      let mainState = "idle";
      let widgetState = props.widgetEnabled ? "idle" : "off";
      let mainLead = "";
      let widgetLead = "";
      let mainText = productDesc;
      let widgetText = widgetDesc;
      if (tone === "running") {
        mainState = "running";
        widgetState = props.widgetEnabled ? "running" : "off";
      } else if (tone === "stale" && !props.verification.article) {
        mainState = "stale";
        widgetState = props.widgetEnabled ? "stale" : "off";
      } else if (tone === "stale") {
        const was = previous.value;
        mainState = changes.value.product
          ? "stale"
          : was
          ? stateOf(was.link)
          : "stale";
        if (!changes.value.product) mainText = `${productDesc} · inchangé`;
        if (changes.value.widget.length) {
          widgetState = "stale";
          widgetLead = "Modifié :";
          widgetText = changes.value.widget.join(" · ");
        } else if (was) {
          widgetState = stateOf(was.widget, props.widgetEnabled);
        }
        if (changes.value.product) mainLead = "Modifié :";
      } else if (props.affiliation) {
        mainState = stateOf(props.affiliation.link);
        widgetState = stateOf(
          props.affiliation.widget,
          props.affiliation.widgetIncluded
        );
      }
      return [
        rowOf({
          key: "main",
          state: mainState,
          image: product.imageUrl || "",
          thumbIcon: mdiShoppingOutline,
          title: "Produit principal",
          tag: "Bouton d’offre",
          lead: mainLead,
          desc: mainText,
          descTitle: productDesc,
        }),
        rowOf({
          key: "widget",
          state: widgetState,
          image: "",
          thumbIcon: mdiViewGridOutline,
          title: "Widget comparatif",
          tag: props.widgetEnabled ? props.widgetType : "Désactivé",
          lead: widgetLead,
          desc: widgetText,
          descTitle: widgetText,
        }),
      ];
    });

    // "Modifier le widget" brings the current selection into view and focus.
    const selectionFocus = ref(0);
    const editWidget = () => {
      selectionFocus.value += 1;
    };

    // Disabled after its creation: the widget stays at Affilizz, unused.
    const createdWidgetExcluded = computed(() => {
      const { article } = props.verification;
      const widget = article && article.affilizz && article.affilizz.widget;
      return !props.widgetEnabled && !!widget && widget.status === "created";
    });

    const contentsIssue = computed(() => {
      const issue = affilizzIssueOf(props.verification.article);
      if (!issue) return null;
      return [issue.label, issue.reason].filter(Boolean).join(" — ");
    });

    return {
      box,
      busy,
      contentsIssue,
      createdWidgetExcluded,
      editWidget,
      guidance,
      colors: { muted: RMR_TEXT_MUTED },
      icons: {
        mdiAlertOutline,
        mdiClose,
        mdiPencil,
        mdiRefresh,
        mdiShieldCheckOutline,
        mdiSwapHorizontal,
      },
      offers,
      recheckHelp: AFFILIATION_RECHECK_HELP,
      recheckWait,
      rows,
      selectionFocus,
      // Row actions follow a read affiliation only (never idle or stale).
      rowActions: computed(
        () => !!props.affiliation && !props.verification.running
      ),
      // The widget row carries its actions instead of a status when offered.
      widgetActions: computed(
        () =>
          !!props.affiliation &&
          !props.verification.running &&
          (offers("disable-widget") || offers("edit-widget"))
      ),
    };
  },
};
</script>

<style lang="scss" scoped>
@import "./_ctc-step.scss";

.ctc-affilizz__intro {
  margin: 6px 0 0;
  color: $rmr-text-muted;
  font-size: $rmr-fs-base;
}

.ctc-affilizz,
.ctc-check {
  margin-top: 18px;
}

// The verification box: a state header, one row per Affilizz content, a foot.
.ctc-check {
  overflow: hidden;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-2xl;
  background: $rmr-surface-0;
}

.ctc-check__head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
}

.ctc-check__shield {
  display: grid;
  width: 38px;
  height: 38px;
  flex: none;
  place-items: center;
  border-radius: $rmr-radius-md;
  background: $rmr-indigo-bg;
}

.ctc-check--ok .ctc-check__shield {
  background: $rmr-green-bg;
}

.ctc-check--pending .ctc-check__shield,
.ctc-check--stale .ctc-check__shield {
  background: $rmr-ca-soft;
}

.ctc-check--error .ctc-check__shield {
  background: $rmr-red-bg;
}

.ctc-check__copy {
  min-width: 0;
  flex: 1;

  strong,
  small {
    display: block;
  }

  strong {
    color: $rmr-text-primary;
    font-size: $rmr-fs-title;
  }

  small {
    margin-top: 1px;
    color: $rmr-text-muted;
    font-size: $rmr-fs-md;
  }
}

.ctc-check__pill {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: $rmr-radius-pill;
  font-size: $rmr-fs-md;
  font-weight: 700;
  white-space: nowrap;
}

.ctc-check--ok .ctc-check__pill {
  background: $rmr-green-bg;
  color: $rmr-green-dark;
}

.ctc-check--pending .ctc-check__pill {
  background: $rmr-ca-soft;
  color: $rmr-ca-dark;
}

.ctc-check--error .ctc-check__pill {
  background: $rmr-red-bg;
  color: $rmr-danger;
}

.ctc-check__verify {
  flex: none;
}

.ctc-check ::v-deep .v-btn {
  letter-spacing: 0 !important;
  text-transform: none !important;
}

.ctc-check__banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 20px 14px;
  padding: 10px 12px;
  border: 1px solid transparent;
  border-radius: $rmr-radius-md;
  font-size: $rmr-fs-body;

  .v-icon {
    margin-top: 3px;
  }

  b {
    font-weight: 700;
  }
}

.ctc-check__banner--error {
  border-color: rgba($rmr-danger, 0.25);
  background: $rmr-red-bg;
  color: darken($rmr-danger, 22%);
}

.ctc-check__banner--warning {
  border-color: $rmr-ca-tint;
  background: $rmr-ca-soft;
  color: darken($rmr-ca-dark, 12%);
}

.ctc-check__rows {
  margin: 0;
  padding: 0;
  border-top: 1px solid $rmr-border;
  list-style: none;
}

.ctc-check__row {
  display: grid;
  grid-template-columns: 28px 44px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
}

.ctc-check__row + .ctc-check__row {
  border-top: 1px solid $rmr-border;
}

.ctc-check__row--stale {
  background: rgba($rmr-ca-soft, 0.6);
}

.ctc-check__row--error {
  background: rgba($rmr-red-bg, 0.35);
}

.ctc-check__state {
  display: grid;
  width: 24px;
  height: 24px;
  place-items: center;
  border-radius: 50%;
}

.ctc-check__state--ok {
  background: $rmr-green-bg;
  color: $rmr-green-dark;
}

.ctc-check__state--error {
  background: $rmr-red-bg;
  color: $rmr-danger;
}

.ctc-check__state--pending,
.ctc-check__state--stale {
  background: $rmr-ca-soft;
  color: $rmr-ca-dark;
}

.ctc-check__state--off {
  background: $rmr-surface-2;
  color: $rmr-text-subtle;
}

.ctc-check__state--idle {
  border: 2px dashed $rmr-border-hover;
}

.ctc-check__thumb {
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  overflow: hidden;
  border-radius: $rmr-radius-md;
  background: $rmr-surface-2;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.ctc-check__info {
  min-width: 0;
}

.ctc-check__title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: $rmr-text-primary;
  font-size: $rmr-fs-body;
  font-weight: 600;
}

.ctc-check__tag {
  padding: 2px 7px;
  border-radius: $rmr-radius-xs;
  background: $rmr-surface-2;
  color: $rmr-text-muted;
  font-size: $rmr-fs-sm;
  font-weight: 700;
}

.ctc-check__desc {
  display: block;
  overflow: hidden;
  color: $rmr-text-muted;
  font-size: $rmr-fs-md;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ctc-check__lead--stale {
  color: $rmr-ca-dark;
}

.ctc-check__actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ctc-check__status {
  font-size: $rmr-fs-md;
  font-weight: 600;
  white-space: nowrap;
}

.ctc-check__status--ok {
  color: $rmr-green-dark;
}

.ctc-check__status--error {
  color: $rmr-danger;
}

.ctc-check__status--pending,
.ctc-check__status--stale {
  color: $rmr-ca-dark;
}

.ctc-check__status--idle,
.ctc-check__status--off {
  color: $rmr-text-muted;
}

// A still bar: the result is on its way (no shimmer animation).
.ctc-check__skeleton {
  display: block;
  width: 70px;
  height: 10px;
  border-radius: $rmr-radius-xs;
  background: $rmr-border;
}

.ctc-check__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 20px;
  border-top: 1px solid $rmr-border;
  background: $rmr-surface-2;
  color: $rmr-text-muted;
  font-size: $rmr-fs-md;

  small {
    color: $rmr-text-subtle;
    font-size: $rmr-fs-sm;
  }
}

@media (max-width: 760px) {
  .ctc-check__head {
    flex-wrap: wrap;
  }

  .ctc-check__copy {
    flex-basis: calc(100% - 52px);
  }

  .ctc-check__pill,
  .ctc-check__verify {
    margin-left: 52px;
  }

  .ctc-check__row {
    grid-template-areas:
      "state thumb info"
      ". . actions";
    grid-template-columns: 24px 40px minmax(0, 1fr);
    padding: 12px 16px;
  }

  .ctc-check__state {
    grid-area: state;
  }

  .ctc-check__thumb {
    width: 40px;
    height: 40px;
    grid-area: thumb;
  }

  .ctc-check__info {
    grid-area: info;
  }

  .ctc-check__actions {
    flex-wrap: wrap;
    grid-area: actions;
  }

  .ctc-check__foot {
    flex-wrap: wrap;
    padding: 10px 16px;
  }
}
</style>
